// La disposición de la oficina (S11, brief §5 + crítica A8/M7; desde el 03-11 la Casa en vez del mundo en píxeles): la
// oficina detrás de todo
// y, flotando encima con la misma superficie y el mismo margen (16 px): el panel del equipo a la izquierda
// (con «Contratar» en su cabecera: un solo sitio) y, si hay un chat abierto, su ventana ocupando el resto,
// pegada al panel a 16 px, con el mundo atenuado detrás. Lo usan la página real y las maquetas.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO, SalaDTO } from '../compartido/vocabulario';
import { Mundo } from '../mundo/Mundo';
import { marcadorEscena } from './situacion';
import { bandejaDe, miBandeja } from './bandejas';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { ListaEquipoS12 } from './ListaEquipoS12';
import { RIEL_PX, RielEquipo } from './RielEquipo';
import { useOficinaStore } from '../store';
import type { OrdenMesas } from '../api';

export const MARGEN_PX = 16;
export const PANEL_PX = 336;

/** Lo que ocupa a la izquierda lo que flota (lista abierta o plegada en columna de caras), con sus márgenes. */
export function useIzquierdaPx(): number {
  const plegado = useOficinaStore((s) => s.panelPlegado);
  return MARGEN_PX + (plegado ? RIEL_PX : PANEL_PX) + MARGEN_PX;
}

export function Escenario({
  salas,
  contratados,
  agenteAbierto,
  resaltado,
  alAbrir,
  alCerrarChat,
  alContratar,
  alAjustarSala,
  alOrdenarMesas,
  widgets,
  chat,
}: {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  agenteAbierto: string | null;
  /** Forzar el resaltado de un agente (maqueta de la tarjeta al pasar el ratón). */
  resaltado?: string | null;
  alAbrir: (agentId: string) => void;
  alCerrarChat: () => void;
  alContratar: (salaId?: string) => void;
  alAjustarSala?: (salaId: string) => void;
  /** 09-10 — ordenar las mesas a mano (solo la página real; las maquetas no ordenan). */
  alOrdenarMesas?: (projectId: string, orden: OrdenMesas) => void;
  /** Rebrand 03-11: la fila de widgets (las pantallas de la pared) arriba de la oficina. */
  widgets?: ReactNode;
  chat: ReactNode;
}) {
  const izquierda = useIzquierdaPx();
  const plegado = useOficinaStore((s) => s.panelPlegado);
  const plegarPanel = useOficinaStore((s) => s.plegarPanel);
  const ajustarPlegado = useOficinaStore((s) => s.ajustarPlegadoAlAncho);
  useEffect(() => {
    const alCambiar = () => ajustarPlegado(window.innerWidth);
    window.addEventListener('resize', alCambiar);
    return () => window.removeEventListener('resize', alCambiar);
  }, [ajustarPlegado]);
  const [sobre, setSobre] = useState<string | null>(null);
  // 04-11 — los widgets tienen su franja arriba y la oficina empieza debajo (si flotaran encima taparían mesas y
  // letreros en pantallas medianas y en el iPad). Se mide su alto real: depende del ancho y de cuántos tienen datos.
  const filaWidgets = useRef<HTMLDivElement>(null);
  const [altoWidgets, setAltoWidgets] = useState(0);
  useEffect(() => {
    const el = filaWidgets.current;
    if (!el) return;
    const medir = () => setAltoWidgets(el.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [widgets]);
  const arriba = widgets && altoWidgets ? MARGEN_PX + altoWidgets : 0;
  // 06-10 — las mesas son de todos: en la escena salen también los agentes de los demás, apagados, con de quién
  // son y sin avisos (lo que les toca es de su dueño; la lista de la izquierda es la bandeja de quien mira).
  const mia = miBandeja(useOficinaStore((s) => s.yo));
  const santi = useSantiPorAgente();
  const marcador = useCallback(
    (c: ContratadoDTO) => {
      if (bandejaDe(c) === mia) return marcadorEscena(c, santi.get(c.agentId));
      return { destaca: false, icono: null, relleno: null, punto: null, late: false, texto: marcadorEscena(c).texto, de: (c.dueno?.nombre ?? 'Pau').split(/\s+/)[0] };
    },
    [mia, santi],
  );
  return (
    <div className="relative h-dvh overflow-hidden bg-oficina-fondo font-sans text-oficina-texto antialiased">
      {/* 04-11 (Pau: «me convencía más con los monigotes en mesas; así me cuesta encontrar mis trabajos»): vuelve la
          oficina con cada agente en su mesa, como la pantalla de inicio del iPhone: cada cosa donde Pau la puso y lo que
          le necesita, marcado sin moverse de sitio. Lo ordenado por estado es la lista de la izquierda. */}
      <div className="absolute inset-x-0 bottom-0" style={{ top: arriba }}>
        <Mundo
          salas={salas}
          contratados={contratados}
          panelIzqPx={izquierda}
          // Con un chat abierto el mundo queda atenuado detrás: ninguna fila de la lista le saca su tarjeta.
          resaltado={resaltado ?? (agenteAbierto ? null : sobre)}
          alAbrir={alAbrir}
          alContratar={alContratar}
          alAjustarSala={alAjustarSala}
          alOrdenarMesas={alOrdenarMesas}
          // El mundo marca lo mismo que la lista («¿qué me toca?»), con su icono y su color.
          marcador={marcador}
        />
      </div>
      {/* Las 4 pantallas de la pared, como widgets en su franja de arriba, a la derecha de la lista. Con un chat abierto
          se ocultan sin desmontarse (la oficina no salta de sitio detrás del chat). */}
      {widgets ? (
        <div ref={filaWidgets} className={cn('pointer-events-auto absolute z-10', agenteAbierto && 'invisible')} style={{ left: izquierda, right: MARGEN_PX, top: MARGEN_PX }}>
          {widgets}
        </div>
      ) : null}

      {agenteAbierto ? (
        <button type="button" tabIndex={-1} aria-label={t('oficinaV2.chat.cerrar')} onClick={alCerrarChat} className="absolute inset-0 z-20 cursor-default bg-black/50 animate-in fade-in-0" />
      ) : null}

      <div className="pointer-events-none absolute z-30 flex flex-col" style={{ left: MARGEN_PX, top: MARGEN_PX, bottom: MARGEN_PX, width: plegado ? RIEL_PX : PANEL_PX }}>
        <div className="pointer-events-auto flex max-h-full min-h-0 flex-col">
          {plegado ? (
            <RielEquipo contratados={contratados} abierto={agenteAbierto} alAbrir={alAbrir} alContratar={() => alContratar()} alDesplegar={() => plegarPanel(false)} alResaltar={setSobre} />
          ) : (
            <ListaEquipoS12 contratados={contratados} abierto={agenteAbierto} alAbrir={alAbrir} alContratar={() => alContratar()} alResaltar={setSobre} alPlegar={() => plegarPanel(true)} />
          )}
        </div>
      </div>

      {agenteAbierto ? (
        <div className="absolute z-30" style={{ left: izquierda, right: MARGEN_PX, top: MARGEN_PX, bottom: MARGEN_PX }}>
          {chat}
        </div>
      ) : null}
    </div>
  );
}
